import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Table, Form, Tabs, Tab, Badge, Spinner } from 'react-bootstrap';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ZAxis, ReferenceArea } from 'recharts';
import authService from '../services/authService';
import businessService from '../services/businessService';
import targetMarketService from '../services/targetMarketService';
import BackToDashboard from '../components/BackToDashboard';
import analyticsService from '../services/analyticsService';

// Custom tab styling for better visibility
const tabStyles = `
  .prominent-tabs .nav-tabs {
    border-bottom: 3px solid #0d6efd;
    margin-bottom: 1rem;
  }
  .prominent-tabs .nav-tabs .nav-link {
    background-color: #e9ecef;
    border: 2px solid #dee2e6;
    border-bottom: none;
    border-radius: 8px 8px 0 0;
    margin-right: 4px;
    padding: 12px 20px;
    font-weight: 600;
    color: #495057;
    transition: all 0.2s ease;
  }
  .prominent-tabs .nav-tabs .nav-link:hover {
    background-color: #d4edff;
    border-color: #0d6efd;
    color: #0d6efd;
  }
  .prominent-tabs .nav-tabs .nav-link.active {
    background-color: #0d6efd;
    border-color: #0d6efd;
    color: white;
  }
  .prominent-tabs .tab-content {
    border: 2px solid #0d6efd;
    border-top: none;
    border-radius: 0 0 8px 8px;
    padding: 1.5rem;
    background-color: #fff;
  }
`;

function TargetMarketDiscovery() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('choose-path'); // choose-path, generate, segments, niches, compare, complete, situation-pain, situation-qualifiers, situation-segments, situation-niches
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Path choice: 'industry' or 'situation'
  const [targetPath, setTargetPath] = useState(null);

  // Stage 1: Segments (industry path)
  const [segments, setSegments] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [keptSegments, setKeptSegments] = useState([]); // Segments to keep during regeneration

  // Stage 2: Niches
  const [nichesBySegment, setNichesBySegment] = useState({});
  const [selectedNiches, setSelectedNiches] = useState([]);

  // Stage 3: Primary selection
  const [primaryNiche, setPrimaryNiche] = useState(null);

  // Scraped target audience (if available)
  const [scrapedTargetAudience, setScrapedTargetAudience] = useState('');

  // Reassurance banner
  const [showReassurance, setShowReassurance] = useState(
    !localStorage.getItem('hideReassuranceBanner')
  );

  const dismissReassurance = () => {
    localStorage.setItem('hideReassuranceBanner', 'true');
    setShowReassurance(false);
  };

  // Situation-based path state
  const [painStatement, setPainStatement] = useState('');
  const [qualifiers, setQualifiers] = useState({
    scale: '',
    stage: '',
    gap: '',
    notFor: ''
  });
  const [situationSegments, setSituationSegments] = useState([]);
  const [preferredNiche, setPreferredNiche] = useState('');

  // Helper to save situation inputs to database
  const saveSituationInputs = async (data) => {
    try {
      await fetch('/api/target-market/save-situation-inputs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });
    } catch (err) {
      console.error('Failed to save situation inputs:', err);
    }
  };

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    // Check authentication
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check if business basics is completed (prerequisite)
    const businessNameResult = await businessService.getField('business_name');
    if (!businessNameResult.success || !businessNameResult.data.field_value) {
      setError('Please complete Business Basics first');
      return;
    }

    // Load scraped target audience if available
    const targetAudienceResult = await businessService.getField('target_audience');
    if (targetAudienceResult.success && targetAudienceResult.data.field_value) {
      setScrapedTargetAudience(targetAudienceResult.data.field_value);
    }

    // Load saved situation inputs (pain statement, qualifiers, and segments) if they exist
    let hasSavedSegments = false;
    try {
      const situationInputsResponse = await fetch('/api/target-market/get-situation-inputs', {
        credentials: 'include'
      });
      if (situationInputsResponse.ok) {
        const situationInputs = await situationInputsResponse.json();
        // Use saved pain statement if available, otherwise fall back to extracted pain points
        if (situationInputs.pain_statement) {
          setPainStatement(situationInputs.pain_statement);
        } else {
          // Fall back to extracted pain points for initial pre-fill
          const painPointsResult = await businessService.getField('pain_points_addressed');
          if (painPointsResult.success && painPointsResult.data.field_value) {
            setPainStatement(painPointsResult.data.field_value);
          }
        }
        // Load saved qualifiers if available
        if (situationInputs.qualifiers) {
          setQualifiers(situationInputs.qualifiers);
        }
        // Load saved preferred niche if available
        if (situationInputs.preferred_niche) {
          setPreferredNiche(situationInputs.preferred_niche);
        }
        // Load saved segments if available - show them on refresh instead of regenerating
        if (situationInputs.segments && situationInputs.segments.length > 0) {
          setSituationSegments(situationInputs.segments);
          hasSavedSegments = true;
        }
      }
    } catch (err) {
      console.error('Failed to load situation inputs:', err);
      // Fall back to extracted pain points
      const painPointsResult = await businessService.getField('pain_points_addressed');
      if (painPointsResult.success && painPointsResult.data.field_value) {
        setPainStatement(painPointsResult.data.field_value);
      }
    }

    // Check if they already have a primary target market
    const primaryResult = await targetMarketService.getPrimaryNiche();
    if (primaryResult.success && primaryResult.data.primary_niche) {
      setPrimaryNiche(primaryResult.data.primary_niche);
      setStage('complete');
      return;
    }

    // Check for saved situation (psychographic) segments first
    if (hasSavedSegments) {
      setTargetPath('situation');
      setStage('situation-segments');
      return;
    }

    // Check for saved industry-specific segments
    try {
      const industrySegmentsResult = await targetMarketService.getSegments();
      if (industrySegmentsResult.success && industrySegmentsResult.data.segments?.length > 0) {
        setSegments(industrySegmentsResult.data.segments);
        setTargetPath('industry');
        setStage('segments');
        return;
      }
    } catch (err) {
      console.error('Failed to load industry segments:', err);
    }
  };

  const generateSegments = async (keepExisting = false) => {
    setLoading(true);
    setError('');
    setStage('segments'); // Move to segments stage immediately to show spinner

    const result = await targetMarketService.generateSegments();

    if (result.success) {
      // Track segments generated
      analyticsService.targetMarket.segmentsGenerated(result.data.segments?.length || 0, {
        path: 'industry',
        kept_existing: keepExisting
      });

      if (keepExisting && keptSegments.length > 0) {
        // Get the actual segment objects to keep
        const segmentsToKeep = segments.filter(s => keptSegments.includes(s.id));
        // Merge kept segments with new ones
        const newSegments = [...segmentsToKeep, ...result.data.segments];
        setSegments(newSegments);
        // Only keep selectedSegments that are still valid (kept segments)
        setSelectedSegments(prev => prev.filter(id => keptSegments.includes(id)));
        setKeptSegments([]); // Clear kept segments
      } else {
        setSegments(result.data.segments);
        // Clear selected segments when regenerating all - old IDs won't be valid
        setSelectedSegments([]);
      }
      setLoading(false);
    } else {
      setError(result.error);
      setLoading(false);
      setStage('generate'); // Go back to generate stage on error
    }
  };

  const toggleKeepSegment = (segmentId) => {
    setKeptSegments(prev => {
      if (prev.includes(segmentId)) {
        return prev.filter(id => id !== segmentId);
      } else {
        return [...prev, segmentId];
      }
    });
  };

  const regenerateUnkept = async () => {
    if (keptSegments.length === 0) {
      setError('Please select at least one segment to keep, or use "Regenerate All" to start fresh');
      return;
    }

    // Keep only the selected segments
    const segmentsToKeep = segments.filter(s => keptSegments.includes(s.id));
    setKeptSegments(segmentsToKeep.map(s => s.id));

    // Regenerate the rest
    await generateSegments(true);
  };

  const toggleSegmentSelection = (segmentId) => {
    setSelectedSegments(prev => {
      if (prev.includes(segmentId)) {
        return prev.filter(id => id !== segmentId);
      } else {
        if (prev.length >= 3) {
          setError('Maximum 3 segments can be selected');
          return prev;
        }
        setError('');
        return [...prev, segmentId];
      }
    });
  };

  const analyzeSelectedSegments = async () => {
    if (selectedSegments.length === 0) {
      setError('Please select at least one segment');
      return;
    }

    setLoading(true);
    setError('');

    const result = await targetMarketService.generateNiches(selectedSegments);

    setLoading(false);

    if (result.success) {
      // Count total niches
      const totalNiches = Object.values(result.data.niches_by_segment || {})
        .reduce((sum, seg) => sum + (seg.niches?.length || 0), 0);

      // Track niches generated
      analyticsService.targetMarket.nichesGenerated(totalNiches, {
        path: 'industry',
        segments_analyzed: selectedSegments.length
      });

      setNichesBySegment(result.data.niches_by_segment);
      setStage('niches');
    } else {
      setError(result.error);
    }
  };

  const toggleNicheSelection = (nicheId, segmentId) => {
    setSelectedNiches(prev => {
      const existing = prev.find(n => n.nicheId === nicheId);
      if (existing) {
        return prev.filter(n => n.nicheId !== nicheId);
      } else {
        if (prev.length >= 4) {
          setError('Maximum 4 niches can be selected for comparison');
          return prev;
        }
        setError('');
        return [...prev, { nicheId, segmentId }];
      }
    });
  };

  const goToComparison = () => {
    if (selectedNiches.length === 0) {
      setError('Please select at least one niche to compare');
      return;
    }
    setStage('compare');
  };

  const setPrimary = async (nicheId) => {
    setLoading(true);
    setError('');

    const result = await targetMarketService.setPrimaryNiche(nicheId);

    setLoading(false);

    if (result.success) {
      // Reload primary niche
      const primaryResult = await targetMarketService.getPrimaryNiche();
      if (primaryResult.success) {
        // Track primary selection and completion
        analyticsService.targetMarket.primarySelected(primaryResult.data.primary_niche?.niche_name, {
          path: 'industry'
        });
        analyticsService.targetMarket.completed({ path: 'industry' });

        setPrimaryNiche(primaryResult.data.primary_niche);
        setStage('complete');
      }
    } else {
      setError(result.error);
    }
  };

  const renderStars = (score) => {
    return '⭐'.repeat(score || 0);
  };

  // Calculate profitability score for niches (1-5 stars)
  const calculateNicheProfitability = (niche) => {
    let score = 0;

    // Conversion likelihood (0-2 points)
    const conv = (niche.conversion_likelihood || '').toLowerCase();
    if (conv.includes('high')) score += 2;
    else if (conv.includes('medium')) score += 1;

    // Estimated LTV (0-2 points)
    const ltv = (niche.estimated_ltv || '').replace(/[$,]/g, '');
    const ltvMatch = ltv.match(/\d+/);
    if (ltvMatch) {
      const ltvValue = parseInt(ltvMatch[0], 10);
      if (ltvValue >= 1000) score += 2;
      else if (ltvValue >= 500) score += 1;
    } else {
      score += 1; // Default if can't parse
    }

    // Cost to reach (0-1 point) - low cost is good
    const cost = (niche.cost_to_reach || '').toLowerCase();
    if (cost.includes('low')) score += 1;

    return Math.min(score, 5);
  };

  const getCostLevel = (costStr) => {
    if (!costStr) return 50;
    if (costStr.toLowerCase().includes('low')) return 25;
    if (costStr.toLowerCase().includes('medium')) return 50;
    if (costStr.toLowerCase().includes('high')) return 75;
    return 50;
  };

  const getConversionLevel = (convStr) => {
    if (!convStr) return 50;
    if (convStr.toLowerCase().includes('high')) return 75;
    if (convStr.toLowerCase().includes('medium')) return 50;
    if (convStr.toLowerCase().includes('low')) return 25;
    return 50;
  };

  // Prepare chart data for segments
  const chartData = segments.map(s => ({
    name: s.segment_name,
    cost: getCostLevel(s.cost_to_reach),
    profitability: (s.profitability_score || 0) * 20, // Scale to 0-100
    size: getConversionLevel(s.conversion_likelihood)
  }));

  // ============ PATH CHOICE ============
  const renderChoosePath = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Target Market Discovery</h2>

        <p className="text-muted mb-4">
          Before we find your ideal customers, I need to understand how your product fits the market.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        {showReassurance && (
          <Alert variant="info" className="d-flex justify-content-between align-items-start">
            <div>
              <strong>💡 Don't overthink it!</strong>
              <p className="mb-0 small">There's no wrong answer here. Pick what feels closest - you can always adjust later based on what your reports show.</p>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white ms-2"
              aria-label="Dismiss"
              onClick={dismissReassurance}
              style={{ fontSize: '0.75rem' }}
            />
          </Alert>
        )}

        <h5 className="mb-4">Which best describes your product?</h5>

        <Row className="g-4">
          <Col md={6}>
            <Card
              className={`h-100 cursor-pointer ${targetPath === 'industry' ? 'border-primary border-2' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setTargetPath('industry')}
            >
              <Card.Body className="p-4">
                <h5 className="text-primary mb-3">Industry-Specific</h5>
                <p className="mb-3">
                  "I built this specifically for <strong>dentists</strong> / <strong>real estate agents</strong> / <strong>restaurants</strong> / etc."
                </p>
                <ul className="text-muted small mb-0">
                  <li>Your product solves problems unique to one industry</li>
                  <li>You speak that industry's language</li>
                  <li>Your marketing targets industry publications, events, associations</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card
              className={`h-100 cursor-pointer ${targetPath === 'situation' ? 'border-primary border-2' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setTargetPath('situation')}
            >
              <Card.Body className="p-4">
                <h5 className="text-primary mb-3">Crosses Industries</h5>
                <p className="mb-3">
                  "Anyone with <strong>this pain</strong> in <strong>this situation</strong> is a fit, regardless of industry."
                </p>
                <ul className="text-muted small mb-0">
                  <li>Your product solves a universal problem</li>
                  <li>Industry doesn't matter - situation does</li>
                  <li>Think: Mailchimp, Stripe, Calendly - horizontal tools</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className="d-flex justify-content-between mt-4">
          <Button variant="outline-secondary" onClick={() => navigate('/dashboard/foundation')}>
            Back to Dashboard
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              // Track path selection
              analyticsService.targetMarket.started(targetPath);

              // Clear shared state when starting a new path
              setSelectedSegments([]);
              setSelectedNiches([]);
              setNichesBySegment({});

              if (targetPath === 'industry') {
                setStage('generate');
              } else if (targetPath === 'situation') {
                setStage('situation-pain');
              }
            }}
            disabled={!targetPath}
          >
            Continue →
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  // ============ SITUATION-BASED PATH ============
  const renderSituationPain = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">What Pain Does Your Product Solve?</h2>

        <p className="text-muted mb-4">
          Describe the #1 problem in your customer's words. What keeps them up at night that YOUR product fixes?
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group className="mb-4">
          <Form.Label><strong>The biggest pain my product solves is...</strong></Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={painStatement}
            onChange={(e) => setPainStatement(e.target.value)}
            placeholder="e.g., 'I know I need to post on social media consistently to grow my business, but I never have time. I feel guilty every day I don't post, while my competitors are everywhere online eating my lunch.'"
          />
          <Form.Text className="text-muted">
            Write this as if your ideal customer is venting to a friend. The more emotional and specific, the better.
          </Form.Text>
        </Form.Group>

        <div className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={() => setStage('choose-path')}>
            ← Back
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              // Save pain statement to database before moving to next screen
              await saveSituationInputs({ pain_statement: painStatement });
              setStage('situation-qualifiers');
            }}
            disabled={!painStatement.trim()}
          >
            Continue →
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const renderSituationQualifiers = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Who Is This For?</h2>

        <p className="text-muted mb-4">
          Help me understand what has to be TRUE about someone for your product to work for them.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group className="mb-4">
          <Form.Label><strong>What scale do they need to operate at?</strong></Form.Label>
          <Form.Select
            value={qualifiers.scale}
            onChange={(e) => setQualifiers({ ...qualifiers, scale: e.target.value })}
          >
            <option value="">Select...</option>
            <option value="local">Local (single city/region)</option>
            <option value="regional">Regional (multiple cities/states)</option>
            <option value="national">National</option>
            <option value="global">Global / International</option>
            <option value="any">Any scale works</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label><strong>What business stage are they in?</strong></Form.Label>
          <Form.Select
            value={qualifiers.stage}
            onChange={(e) => setQualifiers({ ...qualifiers, stage: e.target.value })}
          >
            <option value="">Select...</option>
            <option value="startup">Startup / Just getting started</option>
            <option value="growing">Growing / Scaling up</option>
            <option value="established">Established / Optimizing</option>
            <option value="any">Any stage works</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label><strong>What gap do they have?</strong></Form.Label>
          <Form.Select
            value={qualifiers.gap}
            onChange={(e) => setQualifiers({ ...qualifiers, gap: e.target.value })}
          >
            <option value="">Select...</option>
            <option value="no-team">No one doing this currently (DIY or nothing)</option>
            <option value="overwhelmed">Has team but they're overwhelmed/inconsistent</option>
            <option value="outsourcing">Currently outsourcing and unhappy with results/cost</option>
            <option value="any">Any of the above</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label><strong>Who is this NOT for?</strong> (disqualifiers)</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={qualifiers.notFor}
            onChange={(e) => setQualifiers({ ...qualifiers, notFor: e.target.value })}
            placeholder="e.g., 'Local-only businesses, companies with dedicated marketing teams, people who enjoy creating content themselves'"
          />
          <Form.Text className="text-muted">
            This helps the AI filter OUT poor fits.
          </Form.Text>
        </Form.Group>

        <hr className="my-4" />

        <Form.Group className="mb-4">
          <Form.Label><strong>Do you already have a specific niche in mind?</strong> <span className="text-muted fw-normal">(Optional)</span></Form.Label>
          <Form.Control
            type="text"
            value={preferredNiche}
            onChange={(e) => setPreferredNiche(e.target.value)}
            placeholder="e.g., Real estate agents, Course creators scaling past $50k, etc."
          />
          <Form.Text className="text-muted">
            If you have a niche you want to evaluate, enter it here. It will be analyzed separately alongside the AI's independent suggestions.
          </Form.Text>
        </Form.Group>

        <div className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={() => setStage('situation-pain')}>
            ← Back
          </Button>
          <Button
            variant="primary"
            onClick={generateSituationSegments}
            disabled={!qualifiers.scale || !qualifiers.stage || !qualifiers.gap || loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Finding Your People...
              </>
            ) : 'Find My Ideal Customers →'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const generateSituationSegments = async () => {
    setLoading(true);
    setError('');

    // Save qualifiers, pain statement, and preferred niche to database before generating
    await saveSituationInputs({ pain_statement: painStatement, qualifiers: qualifiers, preferred_niche: preferredNiche });

    try {
      // Build the list of API calls
      const apiCalls = [
        // Always call the main situation segments endpoint
        fetch('/api/target-market/generate-situation-segments', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            pain_statement: painStatement,
            qualifiers: qualifiers
          })
        })
      ];

      // If preferred niche is provided, also analyze it in parallel
      if (preferredNiche.trim()) {
        apiCalls.push(
          fetch('/api/target-market/analyze-preferred-niche', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              preferred_niche: preferredNiche.trim(),
              pain_statement: painStatement,
              qualifiers: qualifiers
            })
          })
        );
      }

      const responses = await Promise.all(apiCalls);

      // Process main segments response
      const mainResponse = responses[0];
      if (!mainResponse.ok) {
        const errorData = await mainResponse.json();
        setError(errorData.error || 'Failed to generate segments');
        setLoading(false);
        return;
      }

      const mainData = await mainResponse.json();
      let allSegments = mainData.segments || [];

      // Process preferred niche response if it exists
      if (responses.length > 1) {
        const preferredResponse = responses[1];
        if (preferredResponse.ok) {
          const preferredData = await preferredResponse.json();
          if (preferredData.segment) {
            // Mark as preferred and prepend to segments list
            const preferredSegment = { ...preferredData.segment, isPreferred: true };
            allSegments = [preferredSegment, ...allSegments];
          }
        }
        // If preferred niche analysis fails, we still show the main segments
      }

      setSituationSegments(allSegments);

      // Track segments generated
      analyticsService.targetMarket.segmentsGenerated(allSegments.length, {
        path: 'situation',
        has_preferred_niche: !!preferredNiche.trim()
      });

      setStage('situation-segments');
    } catch (err) {
      setError('Network error: ' + err.message);
    }

    setLoading(false);
  };

  const renderSituationSegments = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Your Ideal Customer Segments</h2>

        <Alert variant="success" className="mb-4">
          <strong>Based on your pain point + qualifiers, here are the people who need your solution MOST:</strong>
        </Alert>

        {error && <Alert variant="danger">{error}</Alert>}

        {situationSegments.length === 0 ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Analyzing...</p>
          </div>
        ) : (
          <>
            <Alert variant="primary" className="mb-3">
              <strong>Click the checkbox on any segment to select it.</strong>
              <br />
              <small>Select ONE segment as your primary focus, or select up to 3 to explore niches within them first.</small>
            </Alert>

            <Alert variant="info" className="mb-3">
              <small>
                <strong>Tip:</strong> If several segments here don't fit your product, go back and be more specific about who your product/service is <strong>NOT</strong> for.
              </small>
            </Alert>

            {situationSegments.map((segment, index) => {
              const isPreferred = segment.isPreferred === true;
              // Normalize urgency to just High/Medium/Low (AI sometimes returns full sentences)
              const rawUrgency = (segment.urgency || '').toLowerCase();
              const urgencyLabel = rawUrgency.includes('high') ? 'High' : rawUrgency.includes('medium') ? 'Medium' : 'Low';
              const showDivider = isPreferred &&
                index < situationSegments.length - 1 &&
                !situationSegments[index + 1]?.isPreferred;

              return (
                <React.Fragment key={index}>
                  <Card
                    className={`mb-3 ${selectedSegments.includes(index) ? 'border-primary border-2' : isPreferred ? 'border-2' : ''}`}
                    style={{
                      cursor: 'pointer',
                      borderColor: isPreferred && !selectedSegments.includes(index) ? '#d4a017' : undefined,
                      backgroundColor: isPreferred ? '#fffdf5' : undefined
                    }}
                    onClick={() => {
                      if (selectedSegments.includes(index)) {
                        setSelectedSegments(selectedSegments.filter(i => i !== index));
                      } else if (selectedSegments.length < 3) {
                        setSelectedSegments([...selectedSegments, index]);
                      }
                    }}
                  >
                    <Card.Body>
                      <div className="d-flex align-items-start">
                        {/* Visual checkbox indicator */}
                        <div
                          className="me-3 mt-1 flex-shrink-0"
                          style={{
                            width: '24px',
                            height: '24px',
                            border: `2px solid ${selectedSegments.includes(index) ? '#0d6efd' : '#adb5bd'}`,
                            borderRadius: '4px',
                            backgroundColor: selectedSegments.includes(index) ? '#0d6efd' : 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}
                        >
                          {selectedSegments.includes(index) && '✓'}
                        </div>
                        <div className="flex-grow-1 d-flex justify-content-between align-items-start">
                        <div>
                          <h5 className="mb-2">
                            {isPreferred && (
                              <Badge
                                bg="warning"
                                text="dark"
                                className="me-2"
                                style={{ fontSize: '0.7rem', verticalAlign: 'middle' }}
                              >
                                Your Preferred Niche
                              </Badge>
                            )}
                            {segment.segment_name}
                          </h5>
                          <p className="text-muted mb-2">{segment.description}</p>
                          <p className="mb-1"><strong>Why they need this:</strong> {segment.why_they_need_it}</p>
                          <p className="mb-0"><strong>Where to find them:</strong> {segment.where_to_find_them}</p>
                        </div>
                        <Badge className="flex-shrink-0 ms-2" bg={urgencyLabel === 'High' ? 'danger' : urgencyLabel === 'Medium' ? 'warning' : 'secondary'}>
                          {urgencyLabel} Urgency
                        </Badge>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                  {showDivider && (
                    <div className="d-flex align-items-center my-3">
                      <hr className="flex-grow-1" />
                      <span className="px-3 text-muted small fw-bold">AI-GENERATED SEGMENTS</span>
                      <hr className="flex-grow-1" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}

            <div className="d-flex justify-content-between mt-4">
              <Button variant="outline-secondary" onClick={() => setStage('situation-qualifiers')}>
                ← Back
              </Button>
              <div className="d-flex gap-2">
                <Button
                  variant="success"
                  onClick={() => saveSegmentAsPrimary()}
                  disabled={selectedSegments.length !== 1 || loading}
                >
                  {selectedSegments.length === 1
                    ? `Save "${situationSegments[selectedSegments[0]]?.segment_name}" as Primary`
                    : 'Select 1 to Save as Primary'}
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => drillIntoSituationNiches()}
                  disabled={selectedSegments.length === 0 || loading}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Drilling Down...
                    </>
                  ) : 'Drill Deeper (Optional)'}
                </Button>
              </div>
            </div>

            <div className="text-center mt-4 pt-3 border-top">
              <small className="text-muted">
                Want to try a different approach?{' '}
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-decoration-underline"
                  onClick={() => {
                    setTargetPath(null);
                    setSituationSegments([]);
                    setSelectedSegments([]);
                    setNichesBySegment({});
                    setSelectedNiches([]);
                    setPainStatement('');
                    setPreferredNiche('');
                    setQualifiers({ scale: '', stage: '', gap: '', notFor: '' });
                    setStage('choose-path');
                  }}
                >
                  Start over with Industry vs Cross-Industry choice
                </Button>
              </small>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );

  const saveSegmentAsPrimary = async () => {
    if (selectedSegments.length !== 1) return;

    setLoading(true);
    const primaryIndex = selectedSegments[0];
    const primarySegment = situationSegments[primaryIndex];

    try {
      // Save ALL segments, marking one as primary
      const response = await fetch('/api/target-market/save-situation-segments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          segments: situationSegments,
          primary_index: primaryIndex,
          pain_statement: painStatement,
          qualifiers: qualifiers
        })
      });

      if (response.ok) {
        // Track primary selection and completion
        analyticsService.targetMarket.primarySelected(primarySegment.segment_name, {
          path: 'situation',
          type: 'segment'
        });
        analyticsService.targetMarket.completed({ path: 'situation' });

        setPrimaryNiche(primarySegment);
        setStage('complete');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save segments');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    }

    setLoading(false);
  };

  const drillIntoSituationNiches = async () => {
    setLoading(true);
    setError('');

    const selectedSegmentData = selectedSegments.map(i => situationSegments[i]);

    try {
      const response = await fetch('/api/target-market/generate-situation-niches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          segments: selectedSegmentData,
          pain_statement: painStatement,
          qualifiers: qualifiers
        })
      });

      if (response.ok) {
        const data = await response.json();

        // Count total niches
        const totalNiches = Object.values(data.niches_by_segment || {})
          .reduce((sum, niches) => sum + (Array.isArray(niches) ? niches.length : 0), 0);

        // Track niches generated
        analyticsService.targetMarket.nichesGenerated(totalNiches, {
          path: 'situation',
          segments_analyzed: selectedSegmentData.length
        });

        setNichesBySegment(data.niches_by_segment || {});
        setStage('situation-niches'); // Use situation-specific niches UI
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate niches');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    }

    setLoading(false);
  };

  const renderSituationNiches = () => {
    // nichesBySegment format: { "Segment Name": [niches array] }
    const segmentEntries = Object.entries(nichesBySegment);

    return (
      <Card className="shadow">
        <Card.Body className="p-4">
          <h2 className="mb-4">Specific Niches Within Your Segments</h2>

          <Alert variant="primary" className="mb-4">
            <strong>Go through each tab below</strong> to explore niches within each segment. Click the checkbox on the ONE niche that best fits your ideal customer to select it as your primary target.
            <br />
            <small className="text-muted">Look for high urgency + high conversion + low cost to reach for the best opportunities.</small>
          </Alert>

          {error && <Alert variant="danger">{error}</Alert>}

          {segmentEntries.length === 0 ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Generating niches...</p>
            </div>
          ) : (
            <div className="prominent-tabs"><Tabs defaultActiveKey="0" className="mb-3">
              {segmentEntries.map(([segmentName, niches], index) => (
                <Tab eventKey={index.toString()} title={segmentName} key={segmentName}>
                  {Array.isArray(niches) && niches.length > 0 ? (
                    niches.map((niche, nicheIndex) => {
                      const isSelected = selectedNiches.some(n => n.niche_name === niche.niche_name);
                      return (
                      <Card
                        key={nicheIndex}
                        className={`mb-3 ${isSelected ? 'border-primary border-2' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedNiches([]);
                          } else {
                            // Only allow ONE selection - replace any existing
                            setSelectedNiches([{ ...niche, segmentName }]);
                          }
                        }}
                      >
                        <Card.Body>
                          <div className="d-flex align-items-start">
                            {/* Visual checkbox indicator */}
                            <div
                              className="me-3 mt-1 flex-shrink-0"
                              style={{
                                width: '24px',
                                height: '24px',
                                border: `2px solid ${isSelected ? '#0d6efd' : '#adb5bd'}`,
                                borderRadius: '4px',
                                backgroundColor: isSelected ? '#0d6efd' : 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '14px'
                              }}
                            >
                              {isSelected && '✓'}
                            </div>
                            <div className="flex-grow-1">
                            <h5 className="mb-2">
                              {niche.niche_name}
                            </h5>
                          {/* Profitability indicators */}
                          <div className="mb-2">
                            {niche.urgency && (
                              <Badge
                                bg={niche.urgency.toLowerCase().includes('high') ? 'danger' : niche.urgency.toLowerCase().includes('medium') ? 'warning' : 'secondary'}
                                className="me-2"
                              >
                                Urgency: {niche.urgency}
                              </Badge>
                            )}
                            {niche.conversion_likelihood && (
                              <Badge
                                bg={niche.conversion_likelihood.toLowerCase().includes('high') ? 'success' : niche.conversion_likelihood.toLowerCase().includes('medium') ? 'warning' : 'secondary'}
                                className="me-2"
                              >
                                Conversion: {niche.conversion_likelihood}
                              </Badge>
                            )}
                            {niche.cost_to_reach && (
                              <Badge
                                bg={niche.cost_to_reach.toLowerCase().includes('low') ? 'success' : niche.cost_to_reach.toLowerCase().includes('medium') ? 'warning' : 'danger'}
                                className="me-2"
                              >
                                Cost to Reach: {niche.cost_to_reach}
                              </Badge>
                            )}
                          </div>
                          <p className="text-muted mb-2">{niche.description}</p>

                          <Row className="mt-3">
                            <Col md={6}>
                              <p className="mb-1"><strong>Specific Pain:</strong></p>
                              <p className="small text-muted">{niche.specific_pain}</p>
                            </Col>
                            <Col md={6}>
                              <p className="mb-1"><strong>What They've Tried:</strong></p>
                              <p className="small text-muted">{niche.what_theyve_tried}</p>
                            </Col>
                          </Row>

                          <Row>
                            <Col md={6}>
                              <p className="mb-1"><strong>Buying Trigger:</strong></p>
                              <p className="small text-muted">{niche.buying_trigger}</p>
                            </Col>
                            <Col md={6}>
                              <p className="mb-1"><strong>Where to Reach Them:</strong></p>
                              <p className="small text-muted">{niche.where_to_reach}</p>
                            </Col>
                          </Row>

                          {niche.key_pain_points && niche.key_pain_points.length > 0 && (
                            <div className="mt-2">
                              <strong>Key Pain Points:</strong>
                              <ul className="small text-muted mb-0">
                                {niche.key_pain_points.map((pain, i) => (
                                  <li key={i}>{pain}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {niche.messaging_angle && (
                            <Alert variant="info" className="mt-3 mb-0 py-2">
                              <strong>Messaging Hook:</strong> {niche.messaging_angle}
                            </Alert>
                          )}
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    );})
                  ) : (
                    <p className="text-muted">No niches generated for this segment.</p>
                  )}
                </Tab>
              ))}
            </Tabs></div>
          )}

          {selectedNiches.length > 0 && (
            <Alert variant="success" className="mt-3">
              <strong>Selected:</strong> {selectedNiches[0].niche_name}
            </Alert>
          )}

          <div className="d-flex justify-content-between mt-4">
            <Button variant="outline-secondary" onClick={() => setStage('situation-segments')}>
              ← Back to Segments
            </Button>
            <Button
              variant="primary"
              onClick={() => saveSituationPrimaryNiche()}
              disabled={selectedNiches.length === 0}
            >
              {selectedNiches.length > 0
                ? `Save "${selectedNiches[0].niche_name}" as Primary Target →`
                : 'Select a niche above to continue'}
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const saveSituationPrimaryNiche = async () => {
    if (selectedNiches.length === 0) return;

    setLoading(true);
    const primary = selectedNiches[0];

    try {
      const response = await fetch('/api/target-market/set-primary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          niche_name: primary.niche_name,
          segment_name: primary.segmentName,
          description: primary.description,
          key_pain_points: primary.key_pain_points || [],
          messaging_angle: primary.messaging_angle,
          where_to_reach: primary.where_to_reach,
          buying_trigger: primary.buying_trigger
        })
      });

      if (response.ok) {
        // Track primary selection and completion
        analyticsService.targetMarket.primarySelected(primary.niche_name, {
          path: 'situation',
          type: 'niche'
        });
        analyticsService.targetMarket.completed({ path: 'situation' });

        setPrimaryNiche(primary);
        setStage('complete');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save primary niche');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    }

    setLoading(false);
  };

  // ============ INDUSTRY-BASED PATH ============
  const renderGenerate = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Target Market Discovery</h2>

        <p className="text-muted mb-4">
          Discover your ideal target market through AI-powered analysis. We'll identify market segments,
          drill down into specific niches, and help you select your primary target audience.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        {scrapedTargetAudience && (
          <Alert variant="info" className="mb-4">
            <strong>From your website:</strong> "{scrapedTargetAudience}"
            <br />
            <small className="text-muted">We'll use this as a starting point to explore deeper market segments.</small>
          </Alert>
        )}

        <div className="mb-4">
          <h5>How it works:</h5>
          <ol>
            <li><strong>Generate Segments:</strong> AI analyzes 10-15 broad market segments based on your business</li>
            <li><strong>Drill Into Niches:</strong> Select up to 3 segments to explore specific niches within each</li>
            <li><strong>Compare & Select:</strong> Compare up to 4 niches and choose your primary target market</li>
          </ol>
        </div>

        <div className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={() => setStage('choose-path')}>
            ← Back
          </Button>
          <Button
            variant="primary"
            onClick={generateSegments}
            disabled={loading}
          >
            {loading ? 'Analyzing Market...' : 'Generate Market Segments'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const renderSegments = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3 text-muted">
              <strong>Analyzing market opportunities...</strong><br />
              This may take 60-90 seconds as AI evaluates 10-15 market segments.
            </p>
          </div>
        )}

        {!loading && (
          <>
            <h2 className="mb-4">Market Segments</h2>

            <Alert variant="info" className="mb-4">
              <strong>Select up to 3 segments to drill into niches.</strong> Check the "Drill" box for segments you want to explore further. The profitability score (⭐) is based on conversion likelihood, estimated lifetime value, and cost to reach.
            </Alert>

            {error && <Alert variant="danger">{error}</Alert>}

        {/* Scatter Chart */}
        {segments.length > 0 && (
          <div className="mb-4">
            <h5>Market Opportunity Map</h5>
            <ResponsiveContainer width="100%" height={400}>
              <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                <CartesianGrid />

                {/* Green sweet spot area (low cost, high profitability) */}
                <ReferenceArea
                  x1={0}
                  x2={37.5}
                  y1={60}
                  y2={100}
                  fill="#d4edda"
                  fillOpacity={0.3}
                  label={{ value: 'Sweet Spot', position: 'top' }}
                />

                <XAxis
                  type="number"
                  dataKey="cost"
                  name="Cost to Reach"
                  domain={[0, 100]}
                  label={{ value: 'Cost to Reach →', position: 'insideBottom', offset: -10 }}
                />
                <YAxis
                  type="number"
                  dataKey="profitability"
                  name="Profitability"
                  domain={[0, 100]}
                  label={{ value: 'Profitability Score', angle: -90, position: 'insideLeft' }}
                />
                <ZAxis type="number" dataKey="size" range={[100, 500]} />
                <Tooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
                          <p style={{ margin: 0, fontWeight: 'bold' }}>{data.name}</p>
                          <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>
                            Profitability: {data.profitability.toFixed(0)}/100<br />
                            Cost: {data.cost}/100
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend wrapperStyle={{ paddingTop: '20px' }} />
                <Scatter name="Market Segments" data={chartData} fill="#0d6efd" />
              </ScatterChart>
            </ResponsiveContainer>
            <small className="text-muted d-block mb-2">
              <strong>Tip:</strong> Hover over any bubble to see segment details.
            </small>
            <small className="text-muted">
              Bubble size = conversion likelihood. <strong style={{color: '#28a745'}}>Green area</strong> = sweet spot (low cost, high profit).
            </small>
          </div>
        )}

        {/* Help text for Keep/Drill columns */}
        <Alert variant="light" className="border mb-3">
          <Row>
            <Col md={6}>
              <strong>Keep:</strong> Check segments you want to preserve. Click "Keep & Regenerate Rest" to get new options for unchecked segments.
            </Col>
            <Col md={6}>
              <strong>Drill:</strong> Check segments you want to explore further (up to 3). Click "Analyze Selected Segments" to see specific niches within them.
            </Col>
          </Row>
          <div className="mt-2 small text-muted">
            <strong>Tip:</strong> Keep and Drill work independently — you can keep some segments while drilling into others!
          </div>
        </Alert>

        {/* Segments Table */}
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th style={{width: '50px'}}>Keep</th>
              <th style={{width: '50px'}}>Drill</th>
              <th>Segment Name</th>
              <th>Market Size</th>
              <th>Conversion</th>
              <th>Cost to Reach</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            {segments.map((segment) => (
              <tr key={segment.id} style={keptSegments.includes(segment.id) ? { backgroundColor: '#b8e6b8' } : {}}>
                <td className="text-center">
                  <Form.Check
                    type="checkbox"
                    checked={keptSegments.includes(segment.id)}
                    onChange={() => toggleKeepSegment(segment.id)}
                    title="Keep this segment when regenerating"
                  />
                </td>
                <td className="text-center">
                  <Form.Check
                    type="checkbox"
                    checked={selectedSegments.includes(segment.id)}
                    onChange={() => toggleSegmentSelection(segment.id)}
                    title="Drill down into niches"
                  />
                </td>
                <td><strong>{segment.segment_name}</strong></td>
                <td>{segment.market_size}</td>
                <td>{segment.conversion_likelihood}</td>
                <td>{segment.cost_to_reach}</td>
                <td>{renderStars(segment.profitability_score)}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        {keptSegments.length > 0 && (
          <Alert variant="info">
            <strong>{keptSegments.length} segment(s) selected to keep.</strong> When you click "Keep & Regenerate Rest",
            these will be preserved and new segments will be generated to replace the others.
          </Alert>
        )}

        <div className="d-flex justify-content-between mt-4">
          <div>
            <Button
              variant="outline-secondary"
              onClick={() => generateSegments(false)}
              disabled={loading}
              className="me-2"
            >
              Regenerate All
            </Button>
            {keptSegments.length > 0 && (
              <Button
                variant="outline-success"
                onClick={regenerateUnkept}
                disabled={loading}
              >
                Keep {keptSegments.length} & Regenerate Rest
              </Button>
            )}
          </div>
          <Button
            variant="primary"
            onClick={analyzeSelectedSegments}
            disabled={loading || selectedSegments.length === 0}
          >
            {loading ? 'Analyzing Niches...' : `Analyze Selected Segments (${selectedSegments.length})`}
          </Button>
        </div>

        <div className="text-center mt-4 pt-3 border-top">
          <small className="text-muted">
            Want to try a different approach?{' '}
            <Button
              variant="link"
              size="sm"
              className="p-0 text-decoration-underline"
              onClick={() => {
                setTargetPath(null);
                setSegments([]);
                setSelectedSegments([]);
                setKeptSegments([]);
                setNichesBySegment({});
                setSelectedNiches([]);
                setStage('choose-path');
              }}
            >
              Start over with Industry vs Cross-Industry choice
            </Button>
          </small>
        </div>
          </>
        )}
      </Card.Body>
    </Card>
  );

  const renderNiches = () => {
    const segmentTabs = Object.entries(nichesBySegment).map(([segmentId, data]) => ({
      segmentId: parseInt(segmentId),
      segmentName: data.segment_name,
      niches: data.niches
    }));

    return (
      <Card className="shadow">
        <Card.Body className="p-4">
          <h2 className="mb-4">Market Niches</h2>

          <Alert variant="primary" className="mb-4">
            <strong>Go through each tab below</strong> to explore the niches within each segment, then select up to 4 niches to compare. The ⭐ Score shows estimated profitability based on conversion likelihood, lifetime value, and cost to reach.
          </Alert>

          {error && <Alert variant="danger">{error}</Alert>}

          <div className="prominent-tabs"><Tabs defaultActiveKey="0" className="mb-3">
            {segmentTabs.map((tab, index) => (
              <Tab eventKey={index.toString()} title={tab.segmentName} key={tab.segmentId}>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th style={{width: '50px'}}>Select</th>
                      <th>Niche Name</th>
                      <th>⭐ Score</th>
                      <th>Market Size</th>
                      <th>Conversion</th>
                      <th>Est. LTV</th>
                      <th>Cost to Reach</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tab.niches
                      .map(niche => ({ ...niche, profitScore: calculateNicheProfitability(niche) }))
                      .sort((a, b) => b.profitScore - a.profitScore)
                      .map((niche) => (
                      <tr key={niche.id}>
                        <td className="text-center">
                          <Form.Check
                            type="checkbox"
                            checked={selectedNiches.some(n => n.nicheId === niche.id)}
                            onChange={() => toggleNicheSelection(niche.id, tab.segmentId)}
                          />
                        </td>
                        <td><strong>{niche.niche_name}</strong></td>
                        <td className="text-center">{renderStars(niche.profitScore)}</td>
                        <td>{niche.market_size}</td>
                        <td>{niche.conversion_likelihood}</td>
                        <td>{niche.estimated_ltv}</td>
                        <td>{niche.cost_to_reach}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Tab>
            ))}
          </Tabs></div>

          <div className="d-flex justify-content-between mt-4">
            <Button variant="outline-secondary" onClick={() => setStage('segments')}>
              Back to Segments
            </Button>
            <Button
              variant="primary"
              onClick={goToComparison}
              disabled={selectedNiches.length === 0}
            >
              Compare Selected Niches ({selectedNiches.length})
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderCompare = () => {
    // Get full niche data for selected niches with profitability scores
    const selectedNicheData = selectedNiches.map(selection => {
      const segmentData = nichesBySegment[selection.segmentId];
      const niche = segmentData.niches.find(n => n.id === selection.nicheId);
      return {
        ...niche,
        segmentName: segmentData.segment_name,
        profitabilityScore: calculateNicheProfitability(niche)
      };
    });

    // Find the best option (highest profitability)
    const maxProfitability = Math.max(...selectedNicheData.map(n => n.profitabilityScore));
    const bestIndices = selectedNicheData
      .map((n, idx) => n.profitabilityScore === maxProfitability ? idx : -1)
      .filter(idx => idx !== -1);

    return (
      <Card className="shadow">
        <Card.Body className="p-4">
          <h2 className="mb-4">Compare & Select Primary Target Market</h2>

          <Alert variant="info" className="mb-4">
            <strong>Review your selected niches side-by-side.</strong> The profitability score (⭐) is based on conversion likelihood, estimated lifetime value, and cost to reach. Higher is better!
          </Alert>

          {error && <Alert variant="danger">{error}</Alert>}

          <Table bordered hover responsive>
            <thead>
              <tr>
                <th style={{ width: '180px' }}>Attribute</th>
                {selectedNicheData.map((niche, idx) => (
                  <th
                    key={idx}
                    style={{
                      backgroundColor: bestIndices.includes(idx) ? '#d4edda' : 'transparent'
                    }}
                  >
                    Option {idx + 1}
                    {bestIndices.includes(idx) && bestIndices.length === 1 && (
                      <Badge bg="success" className="ms-2">Best</Badge>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Profitability Score - First row for emphasis */}
              <tr style={{ backgroundColor: '#fff8e6' }}>
                <td><strong>⭐ Profitability Score</strong></td>
                {selectedNicheData.map((niche, idx) => (
                  <td
                    key={idx}
                    className="text-center"
                    style={{
                      fontSize: '1.2rem',
                      backgroundColor: bestIndices.includes(idx) ? '#d4edda' : '#fff8e6'
                    }}
                  >
                    {renderStars(niche.profitabilityScore)}
                    <div className="small text-muted">{niche.profitabilityScore}/5</div>
                  </td>
                ))}
              </tr>
              <tr>
                <td><strong>Niche Name</strong></td>
                {selectedNicheData.map((niche, idx) => (
                  <td key={idx} style={{ backgroundColor: bestIndices.includes(idx) ? '#d4edda' : 'transparent' }}>
                    <strong>{niche.niche_name}</strong>
                  </td>
                ))}
              </tr>
              <tr>
                <td><strong>Parent Segment</strong></td>
                {selectedNicheData.map((niche, idx) => (
                  <td key={idx} style={{ backgroundColor: bestIndices.includes(idx) ? '#d4edda' : 'transparent' }}>
                    <Badge bg="secondary">{niche.segmentName}</Badge>
                  </td>
                ))}
              </tr>
              <tr>
                <td><strong>Market Size</strong></td>
                {selectedNicheData.map((niche, idx) => (
                  <td key={idx} style={{ backgroundColor: bestIndices.includes(idx) ? '#d4edda' : 'transparent' }}>
                    {niche.market_size}
                  </td>
                ))}
              </tr>
              <tr>
                <td><strong>Conversion Likelihood</strong></td>
                {selectedNicheData.map((niche, idx) => (
                  <td key={idx} style={{ backgroundColor: bestIndices.includes(idx) ? '#d4edda' : 'transparent' }}>
                    {niche.conversion_likelihood}
                  </td>
                ))}
              </tr>
              <tr>
                <td><strong>Estimated LTV</strong></td>
                {selectedNicheData.map((niche, idx) => (
                  <td key={idx} style={{ backgroundColor: bestIndices.includes(idx) ? '#d4edda' : 'transparent' }}>
                    {niche.estimated_ltv}
                  </td>
                ))}
              </tr>
              <tr>
                <td><strong>Cost to Reach</strong></td>
                {selectedNicheData.map((niche, idx) => (
                  <td key={idx} style={{ backgroundColor: bestIndices.includes(idx) ? '#d4edda' : 'transparent' }}>
                    {niche.cost_to_reach}
                  </td>
                ))}
              </tr>
              <tr>
                <td><strong>Key Pain Points</strong></td>
                {selectedNicheData.map((niche, idx) => (
                  <td key={idx} style={{ backgroundColor: bestIndices.includes(idx) ? '#d4edda' : 'transparent' }}>
                    <ul className="mb-0">
                      {niche.key_pain_points.map((pain, i) => (
                        <li key={i}><small>{pain}</small></li>
                      ))}
                    </ul>
                  </td>
                ))}
              </tr>
              <tr>
                <td><strong>Why They Convert</strong></td>
                {selectedNicheData.map((niche, idx) => (
                  <td key={idx} style={{ backgroundColor: bestIndices.includes(idx) ? '#d4edda' : 'transparent' }}>
                    <small>{niche.why_they_convert}</small>
                  </td>
                ))}
              </tr>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <td><strong>Select as Primary</strong></td>
                {selectedNicheData.map((niche, idx) => (
                  <td key={idx} className="text-center" style={{ backgroundColor: bestIndices.includes(idx) ? '#c3e6cb' : '#f8f9fa' }}>
                    <Button
                      variant={bestIndices.includes(idx) ? 'success' : 'outline-success'}
                      size="sm"
                      onClick={() => setPrimary(niche.id)}
                      disabled={loading}
                    >
                      {loading ? 'Setting...' : bestIndices.includes(idx) ? '✓ Set as Primary' : 'Set Primary'}
                    </Button>
                  </td>
                ))}
              </tr>
            </tbody>
          </Table>

          <div className="d-flex justify-content-between mt-4">
            <Button variant="outline-secondary" onClick={() => setStage('niches')}>
              Back to Niches
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderComplete = () => {
    // Detect if this is a situation-based primary (has description/why_they_need_it instead of market_size/conversion_likelihood)
    const isSituationBased = primaryNiche && (primaryNiche.is_situation_based || primaryNiche.why_they_need_it || primaryNiche.where_to_find_them);

    return (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Primary Target Market Selected</h2>

        <Alert variant="success">
          Your primary target market has been set!
        </Alert>

        {primaryNiche && (
          <Card className="mb-4 border-success">
            <Card.Body>
              <h5 className="mb-3">Your Primary Target Market:</h5>
              <h4>{primaryNiche.niche_name || primaryNiche.segment_name}</h4>
              {primaryNiche.segment_name && primaryNiche.niche_name && primaryNiche.niche_name !== primaryNiche.segment_name && (
                <p className="text-muted">from {primaryNiche.segment_name} segment</p>
              )}

              {isSituationBased ? (
                <>
                  {primaryNiche.description && (
                    <p className="mt-3">{primaryNiche.description}</p>
                  )}
                  <Row className="mt-3">
                    <Col md={6}>
                      {primaryNiche.estimated_size && (
                        <p><strong>Estimated Market Size:</strong> {primaryNiche.estimated_size}</p>
                      )}
                      {primaryNiche.urgency && (
                        <p><strong>Urgency:</strong> {primaryNiche.urgency}</p>
                      )}
                      {primaryNiche.thinking_style && (
                        <p><strong>Thinking Style:</strong> {primaryNiche.thinking_style}</p>
                      )}
                      {primaryNiche.sophistication_level && (
                        <p><strong>Sophistication:</strong> {primaryNiche.sophistication_level}</p>
                      )}
                    </Col>
                    <Col md={6}>
                      {primaryNiche.why_they_need_it && (
                        <p><strong>Why They Need This:</strong><br />{primaryNiche.why_they_need_it}</p>
                      )}
                      {primaryNiche.where_to_find_them && (
                        <p><strong>Where to Find Them:</strong><br />{primaryNiche.where_to_find_them}</p>
                      )}
                    </Col>
                  </Row>
                  {primaryNiche.competitor_frustrations && (
                    <div className="mt-3">
                      <p><strong>Competitor Frustrations:</strong> {primaryNiche.competitor_frustrations}</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Row className="mt-4">
                    <Col md={6}>
                      <p><strong>Market Size:</strong> {primaryNiche.market_size}</p>
                      <p><strong>Conversion Likelihood:</strong> {primaryNiche.conversion_likelihood}</p>
                      <p><strong>Estimated LTV:</strong> {primaryNiche.estimated_ltv}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Cost to Reach:</strong> {primaryNiche.cost_to_reach}</p>
                      <p><strong>Why They Convert:</strong><br />{primaryNiche.why_they_convert}</p>
                    </Col>
                  </Row>

                  <div className="mt-3">
                    <p><strong>Key Pain Points:</strong></p>
                    <ul>
                      {primaryNiche.key_pain_points && primaryNiche.key_pain_points.map((pain, i) => (
                        <li key={i}>{pain}</li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        )}

        <div className="d-flex justify-content-between">
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => navigate('/dashboard/foundation')}>
              Back to Dashboard
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => {
                // Clear all state and go back to choose-path
                setTargetPath(null);
                setSegments([]);
                setSelectedSegments([]);
                setKeptSegments([]);
                setSituationSegments([]);
                setNichesBySegment({});
                setSelectedNiches([]);
                setPrimaryNiche(null);
                setPreferredNiche('');
                setStage('choose-path');
              }}
            >
              Redo Target Market Discovery
            </Button>
          </div>
          <Button
            variant="primary"
            onClick={() => navigate('/customer-journey')}
          >
            Next: Customer Journey →
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
  };

  // Get path and step indicator
  const getPathIndicator = () => {
    if (stage === 'choose-path') return null;
    if (stage === 'complete') return { path: 'Complete', step: null };

    // Industry-specific path
    if (['generate', 'segments', 'niches', 'compare'].includes(stage)) {
      const steps = { generate: 1, segments: 2, niches: 3, compare: 4 };
      return { path: 'Industry-Specific', step: steps[stage], total: 4 };
    }

    // Psychographic/Situation path
    if (stage.startsWith('situation-')) {
      const steps = { 'situation-pain': 1, 'situation-qualifiers': 2, 'situation-segments': 3, 'situation-niches': 4 };
      return { path: 'Cross-Industry (Psychographic)', step: steps[stage], total: 4 };
    }

    return null;
  };

  const pathInfo = getPathIndicator();

  return (
    <Container className="py-5">
      <style>{tabStyles}</style>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <BackToDashboard module="foundation" />
        {pathInfo && (
          <Badge
            bg="secondary"
            style={{ fontSize: '0.85rem', fontWeight: 'normal', padding: '6px 12px' }}
          >
            {pathInfo.path}{pathInfo.step ? `: Step ${pathInfo.step} of ${pathInfo.total}` : ''}
          </Badge>
        )}
      </div>
      <Row className="justify-content-center">
        <Col md={12} lg={11} xl={10}>
          {stage === 'choose-path' && renderChoosePath()}
          {stage === 'generate' && renderGenerate()}
          {stage === 'segments' && renderSegments()}
          {stage === 'niches' && renderNiches()}
          {stage === 'compare' && renderCompare()}
          {stage === 'complete' && renderComplete()}
          {/* Situation-based path */}
          {stage === 'situation-pain' && renderSituationPain()}
          {stage === 'situation-qualifiers' && renderSituationQualifiers()}
          {stage === 'situation-segments' && renderSituationSegments()}
          {stage === 'situation-niches' && renderSituationNiches()}
        </Col>
      </Row>
    </Container>
  );
}

export default TargetMarketDiscovery;
